import { BasicInput } from '@/view/components/BasicInput'
import { Commission } from '@/app/entities/Team'
import { useState, useMemo } from 'react'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/view/components/TabsTable'
import { moneyMask, formatMoneyValue } from '@/app/utils/formatMoneyValue'
import { TriStateEnum } from '@/app/entities/TriState'
import { ProductsConfigResponseNew } from '@/app/services/configService/getProductsConfig'
import { UserRoleEnum } from '@/app/services/usersService/user'
import { PartnerType } from '@/app/utils/partners.enum'

export type CommissionType = 'seller' | 'leader' | 'supervisor'

interface CommissionFormProps {
  commission: Commission[]
  onChange?: (
    index: number,
    type: CommissionType,
    value: number,
    valueIndex?: number,
  ) => void
  readOnly?: boolean
  visibleCommissions?: CommissionType[]
  productsConfig?: ProductsConfigResponseNew | null
  userRole?: UserRoleEnum
}

export const CommissionForm = ({
  commission,
  onChange,
  readOnly = false,
  visibleCommissions = ['supervisor', 'leader', 'seller'],
  productsConfig,
  userRole,
}: CommissionFormProps) => {
  const isAdmin = userRole === UserRoleEnum.SUPER_ADMIN || userRole === UserRoleEnum.ADMIN

  const visibleTabs = useMemo(() => {
    const tabs: string[] = ['fgts']
    const checkTab = (partner: PartnerType, tabName: string) => {
      const state = productsConfig?.products?.[partner]?.state
      if (state === TriStateEnum.FULL) {
        tabs.push(tabName)
      } else if (state === TriStateEnum.ON && isAdmin) {
        tabs.push(tabName)
      }
    }
    checkTab(PartnerType.CREFAZ, 'crefaz')
    checkTab(PartnerType.SALVCAR, 'salvcar')
    checkTab(PartnerType.CREFISA, 'crefisa')
    checkTab(PartnerType.PAN, 'pan')
    return tabs
  }, [productsConfig, isAdmin])

  const [activeTab, setActiveTab] = useState(visibleTabs[0] || 'fgts')

  const isVisible = (type: CommissionType) => visibleCommissions.includes(type)

  const configuredTables = (commission || []).map((table, index) => ({
    ...table,
    index,
  }))

  const fgtsTables = configuredTables.filter(
    (t) =>
      !t.name?.toLowerCase().includes('crefaz') &&
      !t.name?.toLowerCase().includes('salvcar') &&
      !t.name?.toLowerCase().includes('crefisa') &&
      !t.name?.toLowerCase().includes('pan'),
  )
  const crefazTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes('crefaz'),
  )
  const salvcarTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes('salvcar'),
  )
  const crefisaTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes('crefisa'),
  )
  const panTables = configuredTables.filter((t) =>
    t.name?.toLowerCase().includes('pan'),
  )

  const tabsConfig = [
    { id: 'fgts', label: 'FGTS', tables: fgtsTables, isSalvcar: false, isCrefaz: false, isCrefisa: false, isPan: false },
    { id: 'crefaz', label: 'Crefaz', tables: crefazTables, isSalvcar: false, isCrefaz: true, isCrefisa: false, isPan: false },
    { id: 'salvcar', label: 'Salvcar', tables: salvcarTables, isSalvcar: true, isCrefaz: false, isCrefisa: false, isPan: false },
    { id: 'crefisa', label: 'Crefisa', tables: crefisaTables, isSalvcar: false, isCrefaz: false, isCrefisa: true, isPan: false },
    { id: 'pan', label: 'Pan', tables: panTables, isSalvcar: false, isCrefaz: false, isCrefisa: false, isPan: true },
  ]

  const renderCards = (
    tables: typeof configuredTables,
    isSalvcar = false,
    isCrefaz = false,
    isCrefisa = false,
    isPan = false,
    showTitle = true,
  ) => {
    const isCurrency = isSalvcar || isCrefisa;

    const validateAndChange = (
      index: number,
      type: 'seller' | 'leader' | 'supervisor',
      newValue: number | undefined,
      _context: any,
      valueIndex?: number,
    ) => {
      onChange?.(index, type, newValue as number, valueIndex)
    }

    const parseInputValue = (inputValue: string): number | undefined => {
      const cleaned = isCurrency
        ? inputValue.replace(/\D/g, '')
        : inputValue.replace(',', '.')
      if (cleaned === '') return undefined
      const num = Number(cleaned)
      return isNaN(num) ? undefined : num
    }

    return (
      <div className="flex flex-col gap-5 py-5">
        {tables.map(
          ({ index, name, seller, leader, supervisor, table, max, values, ...rest }) => {
            const placeholderSeller = (rest as any).placeholderSeller
            const placeholderLeader = (rest as any).placeholderLeader
            const placeholderSupervisor = (rest as any).placeholderSupervisor
            if (
              values &&
              values.length > 0 &&
              (isCrefaz || isSalvcar || isCrefisa || isPan)
            ) {
              return values.map((value, valueIndex) => (
                <div
                  key={`${table}-${valueIndex}`}
                  className="flex w-full flex-col rounded-xl border bg-card p-4 text-card-foreground"
                >
                  <h3 className="text-lg font-semibold">
                    {isCurrency
                      ? `${isSalvcar ? 'Veículos a' : 'A'} partir de ${formatMoneyValue(value.value)}`
                      : `A partir de ${value.value} parcelas`}
                  </h3>
                  {value?.max && <p className="text-xs text-black04">
                    Máximo{' '}
                    {isCurrency ? formatMoneyValue(value.max || 0) : `${value.max}%`}
                  </p>}
                  <div className="mt-3 flex gap-4">
                    {isVisible('seller') && (
                      <div className="flex-1">
                        <div className="flex gap-2">
                          <p className="text-xs font-bold">Comissão</p>
                          <div className="flex items-center gap-1">
                            <div className="h-[5px] w-[5px] rounded-full bg-blue-600" />
                            <p className="text-xs font-bold text-info">Vendedor</p>
                          </div>
                        </div>
                        <BasicInput
                          name={`commission_${index}_values_${valueIndex}_seller`}
                          {...(isCurrency
                            ? { value: value.seller !== undefined && value.seller !== null ? formatMoneyValue(value.seller) : '' }
                            : { defaultValue: value.seller !== undefined && value.seller !== null ? value.seller : '' }
                          )}
                          placeholder={
                            (value as any).placeholderSeller
                              ? isCurrency
                                ? formatMoneyValue((value as any).placeholderSeller)
                                : String((value as any).placeholderSeller)
                              : undefined
                          }
                          onChange={(e) => {
                            const val = parseInputValue(e.target.value)
                            validateAndChange(index, 'seller', val, value, valueIndex)
                          }}
                          style={{ backgroundColor: '#F7F7F7' }}
                          variant={isCurrency ? 'default' : 'percentage'}
                          onInput={isCurrency ? moneyMask : undefined}
                          disabled={readOnly}
                        />
                      </div>
                    )}
                    {isVisible('leader') && (
                      <div className="flex-1">
                        <div className="flex gap-2">
                          <p className="text-xs font-bold">Comissão</p>
                          <div className="flex items-center gap-1">
                            <div className="h-[5px] w-[5px] rounded-full bg-red-500" />
                            <p className="text-xs font-bold text-red-500">Líder</p>
                          </div>
                        </div>
                        <BasicInput
                          name={`commission_${index}_values_${valueIndex}_leader`}
                          {...(isCurrency
                            ? { value: value.leader !== undefined && value.leader !== null ? formatMoneyValue(value.leader) : '' }
                            : { defaultValue: value.leader !== undefined && value.leader !== null ? value.leader : '' }
                          )}
                          placeholder={
                            (value as any).placeholderLeader
                              ? isCurrency
                                ? formatMoneyValue((value as any).placeholderLeader)
                                : String((value as any).placeholderLeader)
                              : undefined
                          }
                          onChange={(e) => {
                            const val = parseInputValue(e.target.value)
                            validateAndChange(index, 'leader', val, value, valueIndex)
                          }}
                          style={{ backgroundColor: '#F7F7F7' }}
                          variant={isCurrency ? 'default' : 'percentage'}
                          onInput={isCurrency ? moneyMask : undefined}
                          disabled={readOnly}
                        />
                      </div>
                    )}
                    {isVisible('supervisor') && (
                      <div className="flex-1">
                        <div className="flex gap-2">
                          <p className="text-xs font-bold">Comissão</p>
                          <div className="flex items-center gap-1">
                            <div className="h-[5px] w-[5px] rounded-full bg-yellow-500" />
                            <p className="text-xs font-bold text-yellow-500">
                              Supervisor
                            </p>
                          </div>
                        </div>
                        <BasicInput
                          name={`commission_${index}_values_${valueIndex}_supervisor`}
                          {...(isCurrency
                            ? { value: value.supervisor !== undefined && value.supervisor !== null ? formatMoneyValue(value.supervisor) : '' }
                            : { defaultValue: value.supervisor !== undefined && value.supervisor !== null ? value.supervisor : '' }
                          )}
                          placeholder={
                            (value as any).placeholderSupervisor
                              ? isCurrency
                                ? formatMoneyValue((value as any).placeholderSupervisor)
                                : String((value as any).placeholderSupervisor)
                              : undefined
                          }
                          onChange={(e) => {
                            const val = parseInputValue(e.target.value)
                            validateAndChange(index, 'supervisor', val, value, valueIndex)
                          }}
                          style={{ backgroundColor: '#F7F7F7' }}
                          variant={isCurrency ? 'default' : 'percentage'}
                          onInput={isCurrency ? moneyMask : undefined}
                          disabled={readOnly}
                        />

                      </div>
                    )}
                  </div>
                </div>
              ))
            }

            return (
              <div
                key={table}
                className="flex w-full flex-col rounded-xl border bg-card p-4 text-card-foreground"
              >
                {showTitle && <h3 className="text-lg font-semibold">{name}</h3>}
                {showTitle && !!max && (
                  <p className="text-xs text-black04">
                    Máximo {isCurrency ? formatMoneyValue(max || 0) : `${max}%`}
                  </p>
                )}
                <div className="mt-3 flex gap-4">
                  {isVisible('seller') && (
                    <div className="flex-1">
                      <div className="flex gap-2">
                        <p className="text-xs font-bold">Comissão</p>
                        <div className="flex items-center gap-1">
                          <div className="h-[5px] w-[5px] rounded-full bg-blue-600" />
                          <p className="text-xs font-bold text-info">Vendedor</p>
                        </div>
                      </div>
                      <BasicInput
                        name={`commission_${index}_seller`}
                        {...(isCurrency
                          ? { value: seller !== undefined && seller !== null ? formatMoneyValue(seller) : '' }
                          : { defaultValue: seller !== undefined && seller !== null ? seller : '' }
                        )}
                        placeholder={
                          placeholderSeller
                            ? isCurrency
                              ? formatMoneyValue(placeholderSeller)
                              : String(placeholderSeller)
                            : undefined
                        }
                        onChange={(e) => {
                          const val = parseInputValue(e.target.value)
                          validateAndChange(index, 'seller', val, { supervisor, leader, seller, max })
                        }}
                        style={{ backgroundColor: '#F7F7F7' }}
                        variant={isCurrency ? 'default' : 'percentage'}
                        onInput={isCurrency ? moneyMask : undefined}
                        disabled={readOnly}
                      />
                    </div>
                  )}
                  {isVisible('leader') && (
                    <div className="flex-1">
                      <div className="flex gap-2">
                        <p className="text-xs font-bold">Comissão</p>
                        <div className="flex items-center gap-1">
                          <div className="h-[5px] w-[5px] rounded-full bg-red-500" />
                          <p className="text-xs font-bold text-red-500">Líder</p>
                        </div>
                      </div>
                      <BasicInput
                        name={`commission_${index}_leader`}
                        {...(isCurrency
                          ? { value: leader !== undefined && leader !== null ? formatMoneyValue(leader) : '' }
                          : { defaultValue: leader !== undefined && leader !== null ? leader : '' }
                        )}
                        placeholder={
                          placeholderLeader
                            ? isCurrency
                              ? formatMoneyValue(placeholderLeader)
                              : String(placeholderLeader)
                            : undefined
                        }
                        onChange={(e) => {
                          const val = parseInputValue(e.target.value)
                          validateAndChange(index, 'leader', val, { supervisor, leader, seller, max })
                        }}
                        style={{ backgroundColor: '#F7F7F7' }}
                        variant={isCurrency ? 'default' : 'percentage'}
                        onInput={isCurrency ? moneyMask : undefined}
                        disabled={readOnly}
                      />
                    </div>
                  )}
                  {isVisible('supervisor') && (
                    <div className="flex-1">
                      <div className="flex gap-2">
                        <p className="text-xs font-bold">Comissão</p>
                        <div className="flex items-center gap-1">
                          <div className="h-[5px] w-[5px] rounded-full bg-yellow-500" />
                          <p className="text-xs font-bold text-yellow-500">
                            Supervisor
                          </p>
                        </div>
                      </div>
                      <BasicInput
                        name={`commission_${index}_supervisor`}
                        {...(isCurrency
                          ? { value: supervisor !== undefined && supervisor !== null ? formatMoneyValue(supervisor) : '' }
                          : { defaultValue: supervisor !== undefined && supervisor !== null ? supervisor : '' }
                        )}
                        placeholder={
                          placeholderSupervisor
                            ? isCurrency
                              ? formatMoneyValue(placeholderSupervisor)
                              : String(placeholderSupervisor)
                            : undefined
                        }
                        onChange={(e) => {
                          const val = parseInputValue(e.target.value)
                          validateAndChange(index, 'supervisor', val, { supervisor, leader, seller, max })
                        }}
                        style={{ backgroundColor: '#F7F7F7' }}
                        variant={isCurrency ? 'default' : 'percentage'}
                        onInput={isCurrency ? moneyMask : undefined}
                        disabled={readOnly}
                      />
                    </div>
                  )}
                </div>
              </div>
            )
          },
        )}
      </div>
    )
  }

  return (
    <Tabs
      defaultValue={activeTab}
      value={activeTab}
      onValueChange={setActiveTab}
    >
      <div className="flex items-center justify-between pt-6">
        <TabsList className="flex items-center gap-8">
          {tabsConfig.filter(tab => visibleTabs.includes(tab.id)).map(tab => (
            <TabsTrigger
              key={tab.id}
              className="pb-2 text-xl data-[state=active]:border-b-2"
              value={tab.id}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>

      {tabsConfig.filter(tab => visibleTabs.includes(tab.id)).map(tab => (
        <TabsContent key={tab.id} value={tab.id}>
          {renderCards(tab.tables, tab.isSalvcar, tab.isCrefaz, tab.isCrefisa, tab.isPan, true)}
        </TabsContent>
      ))}
    </Tabs>
  )
}
